/**
 * API Client with Cognito Authentication
 * Automatically includes Cognito tokens in API requests
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import JSZip from "jszip";
import {
  User,
  BoundaryData,
  GroupComparisonRequest,
  GroupComparisonResponse,
  AttributeHistoryItem,
  KpiData,
  AnnualNEEData,
  MonthlyNEEData,
  PolygonData,
} from '../types/schema';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

// Debug log
console.log('API_BASE_URL:', API_BASE_URL);
console.log('Environment:', import.meta.env.VITE_API_BASE_URL);

// Helper function to get auth headers
const getAuthHeaders = async (selectedAccountId?: number) => {
  try {
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString();
    const idToken = session.tokens?.idToken;

    if (accessToken && idToken) {
      // Extract custom attributes from ID token
      const accountId = idToken.payload['custom:accountId'];
      const isAdmin = idToken.payload['custom:isAdmin'];

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      };

      // Add custom headers for tenant context
      // For admin users, use selectedAccountId if provided, otherwise use their own accountId
      const finalAccountId = selectedAccountId || accountId;
      if (finalAccountId) {
        headers['X-Account-ID'] = finalAccountId.toString();
      }
      if (isAdmin) {
        headers['X-Is-Admin'] = String(isAdmin);
      }

      return headers;
    }
  } catch (error) {
    console.warn('Failed to get auth session:', error);
  }

  // Fallback headers without auth
  return {
    'Content-Type': 'application/json',
  };
};

// Authenticated API client
export const api = {
  async get<T = unknown>(endpoint: string, selectedAccountId?: number): Promise<T> {
    const headers = await getAuthHeaders(selectedAccountId);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  },

  async post<T = unknown>(endpoint: string, data: unknown, selectedAccountId?: number): Promise<T> {
    const headers = await getAuthHeaders(selectedAccountId);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorBody = await response.json();
        if (errorBody?.error) errorMessage = errorBody.error;
        else if (errorBody?.message) errorMessage = errorBody.message;
        else if (errorBody?.Message) errorMessage = errorBody.Message;
      } catch { /* ignore parse errors, use default error message */ }
      throw new Error(errorMessage);
    }
    return response.json();
  },

  async put<T = unknown>(endpoint: string, data: unknown, selectedAccountId?: number): Promise<T> {
    const headers = await getAuthHeaders(selectedAccountId);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  },

  async delete<T = unknown>(endpoint: string, selectedAccountId?: number): Promise<T> {
    const headers = await getAuthHeaders(selectedAccountId);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
    },

   async downloadCsvFromZipWithPostApi(
    endpoint: string,
    data: unknown,
    selectedAccountId?: number
    ): Promise<{ blob: Blob; filename: string }> {

    const headers = await getAuthHeaders(selectedAccountId);

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "POST",
    headers,
    body: JSON.stringify(data),
    });

    if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    if (errorData?.message) {
      throw new Error(errorData.message);
    }
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Backend sends Base64 ZIP
    const base64Data = await response.text();

    // Base64 → Uint8Array
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
    }

    // Unzip
    const zip = await JSZip.loadAsync(bytes);

    // Assume backend always sends one CSV
    const csvFileName = Object.keys(zip.files).find(name =>
    name.endsWith(".csv")
    );

    if (!csvFileName) {
    throw new Error("CSV file not found in ZIP");
    }

    const csvText = await zip.files[csvFileName].async("string");

      const csvBlob = new Blob([csvText], { type: "text/csv" });

      return {
        blob: csvBlob,
        filename: csvFileName,
      };
   },

    async downloadWithPostApi(endpoint: string, data: unknown, selectedAccountId?: number): Promise<{blob: Blob, filename: string}> {
    const headers = await getAuthHeaders(selectedAccountId);

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      // Try to parse error response as JSON
      const errorData = await response.json().catch(() => null);
      if (errorData && errorData.message) {
        throw new Error(errorData.message);
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const contentDisposition = response.headers.get('content-disposition');
    let filename = 'carbonspace_polygon_info.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch.length > 1) {
        filename = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    return { blob, filename };
  },

    async download(endpoint: string, selectedAccountId?: number): Promise<{blob: Blob, filename: string}> {
    const headers = await getAuthHeaders(selectedAccountId);
    // Not sending JSON
    delete headers['Content-Type'];

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers,
    });

    if (!response.ok) {
      // Try to parse error response as JSON
      const errorData = await response.json().catch(() => null);
      if (errorData && errorData.message) {
        throw new Error(errorData.message);
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const contentDisposition = response.headers.get('content-disposition');
    let filename = 'dashboard_data.csv'; // fallback filename
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch.length > 1) {
        filename = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    return { blob, filename };
  },
};

// Public API client (no auth required)
export const publicApi = {
  async post<T = unknown>(endpoint: string, data: unknown): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  },
};

// Data Explorer APIs
interface PaginationData {
  hasMore: boolean;
  lastPolygonId?: string | number;
  pageSize?: number;
}

interface PaginatedResponse {
  data: [];
  pagination: PaginationData
}

type FetchAllPolygonsParams = {
  urlParams: URLSearchParams;
  selectedAccountId?: number;
};

const fetchAllPolygons = async ({
  urlParams,
  selectedAccountId,
}: FetchAllPolygonsParams): Promise<unknown[]> => {
  const allData: unknown[] = [];
  let hasMore = true;

  while (hasMore) {
    const response = await api.get(
      `/polygons?${urlParams.toString()}`,
      selectedAccountId
    ) as PaginatedResponse;

    allData.push(...(response.data || []));

    hasMore = response.pagination.hasMore;

    if (response.pagination.lastPolygonId !== undefined) {
      urlParams.set(
        'last_polygonid',
        String(response.pagination.lastPolygonId)
      );
    }

    if (response.pagination.pageSize !== undefined) {
      urlParams.set(
        'page_size',
        String(response.pagination.pageSize)
      );
    }
    console.log('urlParams_polygons:', `/polygons?${urlParams.toString()}`);
  }

  return allData;
};


// Data Explorer APIs
export const getExplorerData = async (params: {
  grouping: string;
  start_year: number;
  end_year: number;
  months?: number[];
  search?: string;
  tags?: string[];
  history_filters?: Record<string, string[]>;
  polygonIds?: (number | string)[];
}, selectedAccountId?: number) => {
  const isNone = params.grouping === 'none' || params.grouping === 'NONE';
  if (isNone) {
    const urlParams = new URLSearchParams({
      startYear: String(params.start_year),
      endYear: String(params.end_year),
    });
    if (params.search) {
      urlParams.append('search', params.search);
    }
    if (params.months && params.months.length > 0) {
      urlParams.append('months', params.months.join(','));
    }
    if (params.tags && params.tags.length > 0) {
      urlParams.append('tags', params.tags.join(','));
    }
    if (params.polygonIds && params.polygonIds.length > 0) {
      urlParams.append('polygonIds', params.polygonIds.join(','));
    }
    if (params.history_filters) {
        Object.entries(params.history_filters).forEach(([key, values]) => {
            if (values.length > 0) {
            urlParams.append(key, values.join(','));
            }
        });
    }
     return fetchAllPolygons({ urlParams, selectedAccountId });
  }

  // Normalize groupBy to backend expected form (lowercase)
  const groupBy = params.grouping.toLowerCase();
  const urlParams = new URLSearchParams({
    groupBy,
    startYear: String(params.start_year),
    endYear: String(params.end_year),
  });
  if (params.search){
      urlParams.append('search', params.search);
  }
  if (params.months && params.months.length > 0) {
    urlParams.append('months', params.months.join(','));
  }
  if (params.tags && params.tags.length > 0) {
    urlParams.append('tags', params.tags.join(','));
  }
  if (params.history_filters) {
    Object.entries(params.history_filters).forEach(([key, values]) => {
        if (values.length > 0) {
        urlParams.append(key, values.join(','));
        }
    });
  }
  const response = await api.get(`/polygons/group?${urlParams.toString()}`, selectedAccountId);
  // Unwrap groups from {data: [...]} response
  return (response as { data: unknown[] }).data || [];
};

interface PolygonAttributeHistoryResponse {
  history: AttributeHistoryItem[];
}

interface PolygonTagsResponse {
  tags: string[];
}

export const getPolygonsByIds = async (
  polygonIds: (number | string)[],
  selectedAccountId?: number
): Promise<PolygonData[]> => {
  if (polygonIds.length === 0) {
    return [];
  }

  const response = await api.post<{ polygons: PolygonData[] }>(
    "/polygons/batch",
    { ids: polygonIds },          // send as JSON body
    selectedAccountId             // if your wrapper still needs this
  );

  return response.polygons || [];
};

export const getPolygonsForGroups = async (
  polygonIds: (number | string)[],
  startYear: number,
  endYear: number,
  months?: number[],
  selectedAccountId?: number
) => {
  if (polygonIds.length === 0) {
    return [];
  }

  // Reuse getExplorerData with grouping='none' and polygonIds
  return getExplorerData({
    grouping: 'none',
    start_year: startYear,
    end_year: endYear,
    months,
    polygonIds,
  }, selectedAccountId);
};

export const getAnalytics = async (params: {
  startYear: number;
  endYear: number;
  months?: number[];
  polygonIds?: (string | number)[];
  groupIds?: string[];
  groupBy?: string;
  tags?: string[];
  history_filters?: Record<string, string[]>;
}, selectedAccountId?: number) => {
  // Build query params
  const urlParams = new URLSearchParams({
    startYear: String(params.startYear),
    endYear: String(params.endYear),
  });

  if (params.months && params.months.length > 0) {
    urlParams.append('months', params.months.join(','));
  }

  if (params.polygonIds && params.polygonIds.length > 0) {
    urlParams.append('polygonIds', params.polygonIds.join(','));
  }

  if (params.groupBy && params.groupBy.toLowerCase() !== 'none' && params.groupIds && params.groupIds.length > 0) {
    urlParams.append('groupIds', params.groupIds.join(','));
    urlParams.append('groupBy', params.groupBy);
  }

  if (params.tags && params.tags.length > 0) {
    urlParams.append('tags', params.tags.join(','));
  }

  if (params.history_filters) {
    Object.entries(params.history_filters).forEach(([key, values]) => {
      if (values.length > 0) {
        urlParams.append(key, values.join(','));
      }
    });
  }

  // Fetch both annual and monthly NEE data in parallel
  const [annualResponse, monthlyResponse, kpiResponse] = await Promise.all([
    api.get<{ data: AnnualNEEData[] }>(`/analytics/nee/annual?${urlParams.toString()}`, selectedAccountId),
    api.get<{ data: MonthlyNEEData[] }>(`/analytics/nee/monthly?${urlParams.toString()}`, selectedAccountId),
    api.get<KpiData>(`/analytics/kpi?${urlParams.toString()}`, selectedAccountId)
  ]);

  // Compute average neeIntensity from annual data
  const annualData = annualResponse.data;
  const computedNeeIntensity = annualData.length > 0
    ? annualData.reduce((sum, item) => sum + item.neeIntensity, 0) / annualData.length
    : 0;

  return {
    annualData,
    monthlyData: monthlyResponse.data,
    kpiData: {
      ...kpiResponse,
      neeIntensityPerYear: computedNeeIntensity,
    },
  };
};

// API endpoints
export const endpoints = {
  health: (selectedAccountId?: number) => api.get<{ status: string }>('/', selectedAccountId),
  polygons: (startYear?: number, endYear?: number, selectedAccountId?: number, is_geometry?: boolean, tags?: string[], countries?: string[]) => {
    const urlParams = new URLSearchParams();
    if (startYear !== undefined) urlParams.append('startYear', startYear.toString());
    if (endYear !== undefined) urlParams.append('endYear', endYear.toString());
    if(is_geometry == false) urlParams.append('is_geometry', "false");
    if (tags && tags.length > 0) urlParams.append('tags', tags.join(','));
    if (countries && countries.length > 0) urlParams.append('country', countries.join(','));

     return fetchAllPolygons({ urlParams, selectedAccountId });

  },
  polygon: (id: number, selectedAccountId?: number) => api.get(`/polygons/${id}`, selectedAccountId),
  polygonAttributesHistory: (id: number, selectedAccountId?: number) => api.get<PolygonAttributeHistoryResponse>(`/polygons/${id}/history`, selectedAccountId),
  polygonTags: (id: number, selectedAccountId?: number) => api.get<PolygonTagsResponse>(`/polygons/${id}/tags`, selectedAccountId),
  // kpi: (params: Record<string, any>, accountId?: number) => api.get('/analytics/kpi', accountId, params),

  // Dashboard endpoints
  dashboardAvailableYears: (selectedAccountId?: number) =>
    api.get<{
      min_year: number | null;
      max_year: number | null;
      years: number[];
    }>('/dashboard/available-years', selectedAccountId),

  dashboardKpis: (startYear: number, endYear: number, selectedAccountId?: number) =>
    api.get<{
      polygonCount: number;
      totalArea: number;
      neeTotal: number;
      neeIntensity: number;
    }>(`/dashboard/kpis?startYear=${startYear}&endYear=${endYear}`, selectedAccountId),

  dashboardAnnualNee: (startYear: number, endYear: number, selectedAccountId?: number) =>
    api.get<{ data: Array<{ year: number; nee_total: number }> }>(
      `/dashboard/nee/annual?startYear=${startYear}&endYear=${endYear}`, selectedAccountId
    ),

  dashboardMonthlyNee: (startYear: number, endYear: number, selectedAccountId?: number) =>
    api.get<{ data: Array<{ month_year: string; nee_total: number }> }>(
      `/dashboard/nee/monthly?startYear=${startYear}&endYear=${endYear}`, selectedAccountId
    ),

  dashboardDownloadAll: (selectedAccountId?: number) =>
    api.download('/dashboard/download_all', selectedAccountId),

  dashboardDownloadNeeData: (polygonIds: number[], startYear: number, endYear: number, selectedAccountId?: number) => {
    const body = {
      polygonIds: polygonIds.join(','),
      startYear: startYear.toString(),
      endYear: endYear.toString(),
    };
    return api.downloadCsvFromZipWithPostApi(`/dashboard/download_nee_data`,body, selectedAccountId);
  },

  dashboardDownloadAttributeHistory: (polygonIds: number[], startYear: number, endYear: number, selectedAccountId?: number) => {
    const body = {
      polygonIds: polygonIds.join(','),
      startYear: startYear.toString(),
      endYear: endYear.toString(),
    };
    return api.downloadWithPostApi(`/dashboard/download_attribute_history`,body, selectedAccountId);
  },

  // Analytics endpoints
  analyticsAnnualNee: (startYear: number, endYear: number, polygonIds?: number[], selectedAccountId?: number) => {
    const polygonIdsParam = polygonIds && polygonIds.length > 0
      ? `&polygonIds=${polygonIds.join(',')}`
      : '';
    return api.get<{ data: AnnualNEEData[] }>(
      `/analytics/nee/annual?startYear=${startYear}&endYear=${endYear}${polygonIdsParam}`, selectedAccountId
    );
  },

  analyticsMonthlyNee: (startYear: number, endYear: number, polygonIds?: number[], selectedAccountId?: number) => {
    const polygonIdsParam = polygonIds && polygonIds.length > 0
      ? `&polygonIds=${polygonIds.join(',')}`
      : '';
    return api.get<{ data: MonthlyNEEData[] }>(
      `/analytics/nee/monthly?startYear=${startYear}&endYear=${endYear}${polygonIdsParam}`, selectedAccountId
    );
  },

  baselineAnalysis: (
    startYear: number,
    endYear: number,
    baselinePeriod: number,
    polygonIds?: (string | number)[],
    months?: number[],
    selectedAccountId?: number
  ) => {
    const params = new URLSearchParams({
      startYear: startYear.toString(),
      endYear: endYear.toString(),
      baselinePeriod: baselinePeriod.toString(),
    });
    if (polygonIds && polygonIds.length > 0) {
      params.append('polygonIds', polygonIds.join(','));
    }
    if (months && months.length > 0) {
      params.append('months', months.join(','));
    }
    const queryString = params.toString();
    return api.get(`/analytics/baseline?${queryString}`, selectedAccountId);
  },

  // Admin endpoints
  adminAccounts: (selectedAccountId?: number) => api.get<{ accounts: Array<{ account_id: number; name: string }> }>('/admin/accounts', selectedAccountId),
  adminAccountDetails: (accountId: number, selectedAccountId?: number) =>
    api.get<{ account_id: number; name: string }>(`/admin/accounts/${accountId}`, selectedAccountId),
  adminGetUsersForAccount: (accountId: string, selectedAccountId?: number) =>
    api.get<{ users: User[] }>(`/admin/accounts/${accountId}/users`, selectedAccountId),
  adminAddUserToAccount: (accountId: string, data: { email: string; given_name: string; family_name: string, full_name: string }, selectedAccountId?: number) =>
    api.post(`/admin/accounts/${accountId}/users`, data, selectedAccountId),
  adminDeleteUser: (username: string, selectedAccountId?: number) =>
    api.delete(`/admin/users/${username}`, selectedAccountId),
  adminResetPassword: (username: string, data: { password: string }, selectedAccountId?: number) =>
    api.post(`/admin/users/${username}/reset-password`, data, selectedAccountId),

  // Public endpoints (no auth required)
  checkUserExists: (email: string) =>
    publicApi.post<{ exists: boolean; message: string }>('/admin/check-user-exists', { email }),
};

// Typeahead suggestions for polygons
export const getPolygonSuggestions = async (searchQuery: string, selectedAccountId?: number) => {
    if (searchQuery.length < 3) {
        return [];
    }
    const response = await api.get<{ name: string; country: string }[]>(
        `/polygons/suggestions?q=${searchQuery}`,
        selectedAccountId
    );
    return response;
};

// Get all tags
export const getAllTags = async (selectedAccountId?: number): Promise<string[]> => {
    const response = await api.get<string[]>(`/data/tags`, selectedAccountId);
    return response;
};

// Get all countries
export const getAllCountries = async (selectedAccountId?: number): Promise<string[]> => {
    const response = await api.get<string[]>(`/data/countries`, selectedAccountId);
    return response;
};

// Get history attribute values for filters
export const getHistoryAttributeValues = async (
  attributes: string[],
  selectedAccountId?: number
): Promise<Record<string, string[]>> => {
  if (attributes.length === 0) {
    return {};
  }
  const attributesStr = attributes.join(',');
  const response = await api.get<{ data: Record<string, string[]> }>(
    `/data/history_attribute_values?attributes=${attributesStr}`,
    selectedAccountId
  );
  return response.data;
};

export const postGroupComparison = async (
  payload: GroupComparisonRequest,
  selectedAccountId?: number
): Promise<GroupComparisonResponse> => {
  return api.post<GroupComparisonResponse>('/analytics/group-comparison', payload, selectedAccountId);
};

export const getBoundaries = async (selectedAccountId?: number): Promise<BoundaryData[]> => {
    return await api.get<BoundaryData[]>('/boundaries', selectedAccountId);
};
